/**
 * @module tools/DynamicJavaScriptLoader
 */
import { secureUrl } from '../lib/UrlUtils';
import Logger from '../tools/Logger';

import { setTimeout } from './Globals';

// for debugging purposes
const log = Logger.child({ component: 'DynamicJavaScriptLoader' }, true);
const TIMEOUT = 10000;
// placeholder, will not query dom for head until first request
let head;

// because this api only deals with injecting scripts into _head_ it will only
// check there for efficiency
export function checkScriptExists(node, uri) {
  if (!node) {
    return false;
  }
  const scripts = node.querySelectorAll('script');
  for (let i = 0; i < scripts.length; ++i) {
    const script = scripts[i];
    if (script.src === uri) return true;
  }
  return false;
}

export function loadFile(uri, timeout = TIMEOUT) {
  const secureUri = secureUrl(uri);
  if (!head) head = document.querySelector('head');
  log.trace(`Attempting to load JavaScript uri "${secureUri}"`);

  return new Promise((resolve, reject) => {
    const el = document.createElement('script');
    el.async = true;

    // ensure a script with the same path doesn't already exist sanity check
    if (checkScriptExists(head, secureUri)) {
      log.info(
        `Attempt to load the same script more than once, not loading again because it already exists: "${secureUri}"`,
      );
      resolve();
    }

    let tid;
    if (timeout) {
      tid = setTimeout(() => {
        el.onload = () => {};
        // to ensure we don't disallow a proper attempt to reload the script later
        // for some reason we remove it
        if (head.contains(el)) {
          head.removeChild(el);
        }
        log.error(`Timeout failed for loading dynamic script "${secureUri}"`);
        reject(
          new Error(`Timeout for dynamic script passed, considered failing after ${timeout}MS`),
        );
      }, timeout);
    }

    el.type = 'text/javascript';
    el.onload = () => {
      clearTimeout(tid);
      log.trace(`Successfully loaded JavaScript uri "${secureUri}"`);
      resolve();
    };
    el.onerror = e => {
      clearTimeout(tid);
      log.info(`Failed to load JavaScript uri "${secureUri}"`);
      // to ensure we don't disallow a proper attempt to reload the script later
      // for some reason we remove it
      head.removeChild(el);
      reject(e);
    };
    el.src = secureUri;
    head.appendChild(el);
  });
}

async function mapSeriesPromises(promises, mapper, concurrent = false) {
  if (concurrent === true) return await Promise.all(promises.map(promise => mapper(promise)));
  let result = [];
  const length = promises.length;
  let resolved;
  for (let i = 0; i < length; i++) {
    resolved = await mapper(await promises[i], i, length);
    result = result.concat(resolved);
  }

  return result;
}

export function loadFiles(files, concurrent = false) {
  return Promise.resolve().then(async () => {
    return await mapSeriesPromises(files, file => loadFile(file), concurrent);
  });
}
